// Self-contained navbar functionality - runs immediately when navbar loads
import { ROUTES } from '../../../routes/Paths';

// Active page detection - now only for sticky navbar (main navbar is controlled by React)
export function updateActivePage(): void {
    // Only update sticky navbar, not main navbar (which is controlled by React)
    const stickyNavLinks = document.querySelectorAll('.stricky-header .nav-link');
    if (stickyNavLinks.length === 0) {
        // Sticky navbar not initialized yet, skip
        return;
    }

    const currentPath = window.location.pathname;
    
    // Remove active from all sticky nav links
    stickyNavLinks.forEach(link => {
        link.classList.remove('active');
    });
    
    // Map clean URLs to their corresponding data-page values
    const urlToPageMap: Record<string, string> = {
        [ROUTES.HOME]: 'index',
        [ROUTES.STORE]: 'store',
        [ROUTES.WORKSHOP]: 'workshop',
        [ROUTES.BUDGET]: 'budget',
        [ROUTES.WE_CALL]: 'wecall',
        [ROUTES.RECRUITMENT]: 'recruitment',
    };
    
    const currentPage = urlToPageMap[currentPath] || currentPath.split('/').pop()?.split('.')[0] || '';
    
    // Special handling for store page with category path
    if (currentPath === '/loja/pneus') {
        // Mark the Pneus/Tires button as active in sticky navbar
        const tiresLink = document.querySelector('.stricky-header [data-category="tires"]');
        if (tiresLink) {
            tiresLink.classList.add('active');
        }
    } else {
        // For other pages, use the standard logic
        const currentLink = document.querySelector(`.stricky-header [data-page="${currentPage}"]:not([data-category])`);
        if (currentLink) {
            currentLink.classList.add('active');
        }
    }
    
    // Handle special case for index.html (home page)
    if (currentPath === '/' || currentPath === '' || currentPath.endsWith('/')) {
        const homeLink = document.querySelector('.stricky-header [data-page="index"]');
        if (homeLink) {
            homeLink.classList.add('active');
        }
    }
}

// Sticky navbar functionality
let stickyMenuInitialized = false;

export function initStickyNavbar(forceUpdate: boolean = false): void {
    const header = document.querySelector('.main-header') as HTMLElement;
    const stickyHeader = document.querySelector('.stricky-header') as HTMLElement;
    const stickyContent = document.querySelector('.sticky-header__content') as HTMLElement;
    
    // If elements don't exist, try again after a short delay
    if (!header || !stickyHeader || !stickyContent) {
        if (!stickyMenuInitialized) {
            setTimeout(() => {
                initStickyNavbar(forceUpdate);
            }, 100);
        }
        return;
    }
    
    // Calculate header height - use a minimum value if header hasn't fully rendered
    const headerHeight = header.offsetHeight || header.getBoundingClientRect().height || 100;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
        
        if (scrollTop > headerHeight) {
            stickyHeader.classList.add('stricked-menu');
            stickyHeader.style.display = 'block';
            stickyHeader.style.position = 'fixed';
            stickyHeader.style.top = '0';
            stickyHeader.style.left = '0';
            stickyHeader.style.width = '100%';
            stickyHeader.style.zIndex = '9999';
            stickyHeader.style.backgroundColor = '#000000';
            stickyHeader.style.boxShadow = '0 2px 10px rgba(0,0,0,0.3)';
            
        // Copy content and update states when forced, initializing, or if content is empty
        const needsUpdate = !stickyMenuInitialized || forceUpdate || !stickyContent.innerHTML.trim();
        if (needsUpdate) {
                const mainMenu = document.querySelector('.main-menu');
                if (mainMenu) {
                    stickyContent.innerHTML = mainMenu.outerHTML;
                    setupStickyMenuHandlers();
            
                // Update active states and switchers
            updateStickyMenuActiveStates();
            updateStickyLanguageSwitcher();
            updateStickyThemeSwitcher();
                    
                    stickyMenuInitialized = true;
            } else {
                // Main menu not ready, try again
                setTimeout(() => {
                    initStickyNavbar(forceUpdate);
                }, 100);
                }
        } else {
            // Just update active states without re-copying content
            updateStickyMenuActiveStates();
            }
        } else {
            stickyHeader.classList.remove('stricked-menu');
            stickyHeader.style.display = 'none';
    }
}

// Set up click handlers for sticky menu
function setupStickyMenuHandlers(): void {
    const stickyNavLinks = document.querySelectorAll('.stricky-header .nav-link');
    stickyNavLinks.forEach(link => {
        link.addEventListener('click', function(this: HTMLElement) {
            // Remove active class from all sticky nav links
            stickyNavLinks.forEach(stickyLink => {
                stickyLink.classList.remove('active');
            });
            
            // Add active class to clicked link
            this.classList.add('active');
            
            // Also update the main menu
            const mainNavLinks = document.querySelectorAll('.main-menu .nav-link');
            mainNavLinks.forEach(mainLink => {
                mainLink.classList.remove('active');
            });
            
            const correspondingMainLink = document.querySelector(`.main-menu [data-page="${this.getAttribute('data-page')}"]`);
            if (correspondingMainLink) {
                correspondingMainLink.classList.add('active');
            }
        });
    });
    
    // Set up language switcher for sticky navbar
    setupStickyLanguageSwitcher();
    
    // Set up theme switcher for sticky navbar
    setupStickyThemeSwitcher();
    
    // Set up search button for sticky navbar
    setupStickySearchButton();
}

// Set up language switcher for sticky navbar
function setupStickyLanguageSwitcher(): void {
    const stickyLanguageButton = document.querySelector('.stricky-header #language-switcher') as HTMLElement;
    if (stickyLanguageButton && (window as any).NavbarContext) {
        // Check if we already have a listener attached
        if (stickyLanguageButton.hasAttribute('data-listener-attached')) {
            return; // Already has listener, don't re-attach
        }
        
        // Change the ID to avoid conflicts with the main navbar
        stickyLanguageButton.id = 'sticky-language-switcher';
        
        // Add click handler for sticky language switcher
        stickyLanguageButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if ((window as any).NavbarContext && (window as any).NavbarContext.setLanguage) {
                const currentLanguage = (window as any).NavbarContext.language;
                const newLanguage = currentLanguage === 'en' ? 'pt' : 'en';
                (window as any).NavbarContext.setLanguage(newLanguage);
                // Update sticky navbar text after language change
                setTimeout(() => {
                    updateStickyNavbarText();
                }, 100);
            }
        });
        
        // Mark as having listener attached
        stickyLanguageButton.setAttribute('data-listener-attached', 'true');
        
        // Update the button content based on current language
        updateStickyLanguageSwitcher();
    }
}

// Update sticky navbar text content to match main navbar
export function updateStickyNavbarText(): void {
    // Force update the sticky navbar with latest content
    stickyMenuInitialized = false;
    initStickyNavbar(true);
}

// Update sticky language switcher button text and flag
// Shows the language you'll switch TO, not the current language
function updateStickyLanguageSwitcher(): void {
    const stickyLanguageButton = document.querySelector('.stricky-header #sticky-language-switcher') as HTMLElement;
    if (stickyLanguageButton && (window as any).NavbarContext) {
        const currentLanguage = (window as any).NavbarContext.language;
        const flagIcon = stickyLanguageButton.querySelector('.flag-icon');
        const languageText = stickyLanguageButton.querySelector('.language-text');
        
        if (flagIcon && languageText) {
            // Show opposite language - what you'll switch to
            if (currentLanguage === 'pt') {
                // Currently Portuguese, show English option
                flagIcon.textContent = '🇬🇧';
                languageText.textContent = 'EN';
            } else {
                // Currently English, show Portuguese option
                flagIcon.textContent = '🇵🇹';
                languageText.textContent = 'PT';
            }
        }
    }
}

// Update active states in sticky menu to match main menu
function updateStickyMenuActiveStates(): void {
    const mainNavLinks = document.querySelectorAll('.main-menu .nav-link');
    const stickyNavLinks = document.querySelectorAll('.stricky-header .nav-link');
    
    if (stickyNavLinks.length === 0) return; // Sticky menu not initialized yet
    
    // Track which link should be active
    let activeDataPage: string | null = null;
    let activeDataCategory: string | null = null;
    
    mainNavLinks.forEach(mainLink => {
        if (mainLink.classList.contains('active')) {
            activeDataPage = mainLink.getAttribute('data-page');
            activeDataCategory = mainLink.getAttribute('data-category');
        }
    });
    
    // Only update if there's an active link
    if (activeDataPage) {
        stickyNavLinks.forEach(stickyLink => {
            const dataPage = stickyLink.getAttribute('data-page');
            const dataCategory = stickyLink.getAttribute('data-category');
            
            const shouldBeActive = 
                dataPage === activeDataPage && 
                (activeDataCategory ? dataCategory === activeDataCategory : !dataCategory);
            
            // Only modify if state needs to change (prevents unnecessary CSS re-renders)
            if (shouldBeActive && !stickyLink.classList.contains('active')) {
                stickyLink.classList.add('active');
            } else if (!shouldBeActive && stickyLink.classList.contains('active')) {
                stickyLink.classList.remove('active');
        }
    });
    }
    
    // Also update the language switcher in sticky navbar
    updateStickyLanguageSwitcher();
    
    // Also update the theme switcher in sticky navbar
    updateStickyThemeSwitcher();
}

// Click event handler for navigation links
export function handleNavClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (target.classList.contains('nav-link') || target.closest('.nav-link')) {
        const clickedLink = target.classList.contains('nav-link') ? target : target.closest('.nav-link') as HTMLElement;
        
        // Only update sticky navbar (main navbar is controlled by React)
        if (clickedLink.closest('.stricky-header')) {
            // Remove active class from all sticky nav links
            document.querySelectorAll('.stricky-header .nav-link').forEach(link => {
            link.classList.remove('active');
        });
        
            // Add active class to clicked sticky link
        clickedLink.classList.add('active');
        
            // Also update main menu to match (for consistency)
            const dataPage = clickedLink.getAttribute('data-page');
            const dataCategory = clickedLink.getAttribute('data-category');
            
            if (dataPage) {
                const mainNavLinks = document.querySelectorAll('.main-menu .nav-link');
                mainNavLinks.forEach(mainLink => {
                    mainLink.classList.remove('active');
                });
                
                let mainLink: Element | null = null;
                if (dataCategory) {
                    mainLink = document.querySelector(`.main-menu [data-page="${dataPage}"][data-category="${dataCategory}"]`);
                } else {
                    mainLink = document.querySelector(`.main-menu [data-page="${dataPage}"]:not([data-category])`);
                }
                
                if (mainLink) {
                    mainLink.classList.add('active');
                }
            }
        }
        
        // Update active page after navigation (for URL-based detection in sticky navbar)
        setTimeout(() => {
            updateActivePage();
        }, 100);
    }
}

// Theme toggle functionality - now handled by theme-context.js
export function initThemeToggle(): void {
    // Theme management is now handled by the external theme-context.js
    // This function is kept for compatibility but the actual work is done by ThemeContext
    if ((window as any).ThemeContext) {
        // ThemeContext is already initialized, just ensure icons are updated
        (window as any).ThemeContext.updateThemeIcons();
    }
}

// Sync mobile language switcher with desktop
export function syncMobileLanguageSwitcher(): void {
    const mobileLangBtn = document.getElementById('language-switcher-mobile') as HTMLElement;
    
    if (mobileLangBtn && (window as any).LanguageContext) {
        // Add click handler for mobile language switcher
        if (!mobileLangBtn.hasAttribute('data-listener-attached')) {
            mobileLangBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const currentLanguage = (window as any).LanguageContext.currentLanguage;
                const newLanguage = currentLanguage === 'en' ? 'pt' : 'en';
                (window as any).LanguageContext.switchLanguage(newLanguage);
            });
            
            // Sync button content with desktop
            function updateMobileLangButton(): void {
                if ((window as any).LanguageContext && mobileLangBtn) {
                    const currentLanguage = (window as any).LanguageContext.currentLanguage;
                    const flagIcon = mobileLangBtn.querySelector('.flag-icon');
                    const languageText = mobileLangBtn.querySelector('.language-text');
                    
                    if (flagIcon && languageText) {
                        if (currentLanguage === 'pt') {
                            flagIcon.textContent = '🇬🇧';
                            languageText.textContent = 'EN';
                        } else {
                            flagIcon.textContent = '🇵🇹';
                            languageText.textContent = 'PT';
                        }
                    }
                }
            }
            
            // Listen for language changes
            window.addEventListener('languageChanged', updateMobileLangButton);
            updateMobileLangButton();
            
            mobileLangBtn.setAttribute('data-listener-attached', 'true');
        }
    }
}

// Sync mobile theme switcher with desktop
export function syncMobileThemeSwitcher(): void {
    const mobileThemeBtn = document.getElementById('theme-toggle-mobile') as HTMLElement;
    
    if (mobileThemeBtn) {
        // Add click handler for mobile theme switcher
        if (!mobileThemeBtn.hasAttribute('data-listener-attached')) {
            // Click handler
            mobileThemeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                if ((window as any).NavbarContext && (window as any).NavbarContext.toggleColorMode) {
                    (window as any).NavbarContext.toggleColorMode();
                } else if ((window as any).ThemeContext && (window as any).ThemeContext.toggleTheme) {
                    (window as any).ThemeContext.toggleTheme();
                } else {
                    // Fallback if contexts not loaded yet
                    const isDark = document.body.classList.contains('dark-theme');
                    document.body.classList.toggle('dark-theme', !isDark);
                    localStorage.setItem('themePreference', !isDark ? 'dark' : 'light');
                }
            }, { capture: true });
            
            // Touch handler for mobile devices
            mobileThemeBtn.addEventListener('touchend', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                if ((window as any).NavbarContext && (window as any).NavbarContext.toggleColorMode) {
                    (window as any).NavbarContext.toggleColorMode();
                } else if ((window as any).ThemeContext && (window as any).ThemeContext.toggleTheme) {
                    (window as any).ThemeContext.toggleTheme();
                } else {
                    // Fallback if contexts not loaded yet
                    const isDark = document.body.classList.contains('dark-theme');
                    document.body.classList.toggle('dark-theme', !isDark);
                    localStorage.setItem('themePreference', !isDark ? 'dark' : 'light');
                }
            }, { capture: true });
            
            // Ensure button is clickable
            mobileThemeBtn.style.pointerEvents = 'auto';
            mobileThemeBtn.style.cursor = 'pointer';
            mobileThemeBtn.style.touchAction = 'manipulation';
            mobileThemeBtn.style.minWidth = '44px';
            mobileThemeBtn.style.minHeight = '44px';
            
            // Sync icon with desktop
            function updateMobileThemeIcon(): void {
                if (mobileThemeBtn) {
                    const icon = mobileThemeBtn.querySelector('#theme-icon-mobile') as HTMLElement;
                    if (icon) {
                        const isDark = document.body.classList.contains('dark-theme');
                        icon.className = isDark ? 'fas fa-sun' : 'fas fa-moon';
                    }
                }
            }
            
            // Listen for theme changes
            window.addEventListener('themeChanged', updateMobileThemeIcon);
            
            // Also listen for body class changes
            const themeObserver = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        updateMobileThemeIcon();
                    }
                });
            });
            
            if (document.body) {
                themeObserver.observe(document.body, {
                    attributes: true,
                    attributeFilter: ['class']
                });
            }
            
            // Initial update
            setTimeout(updateMobileThemeIcon, 100);
            updateMobileThemeIcon();
            
            mobileThemeBtn.setAttribute('data-listener-attached', 'true');
        }
    }
}

// Ensure mobile menu button works even if jQuery isn't loaded
export function ensureMobileMenuWorks(): void {
    let isToggling = false; // Prevent rapid clicking
    
    // Function to toggle menu
    function toggleMobileMenu(e?: Event): void {
        if (isToggling) return; // Prevent rapid clicking
        isToggling = true;
        
        if (e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
        
        const mobileNavWrapper = document.querySelector('.mobile-nav__wrapper') as HTMLElement;
        const mobileTogglers = document.querySelectorAll('.mobile-nav__toggler');
        if (mobileNavWrapper) {
            const isExpanded = mobileNavWrapper.classList.contains('expanded');
            if (isExpanded) {
                mobileNavWrapper.classList.remove('expanded');
                document.body.classList.remove('locked');
                mobileTogglers.forEach(toggler => {
                    toggler.setAttribute('aria-expanded', 'false');
                });
            } else {
                mobileNavWrapper.classList.add('expanded');
                document.body.classList.add('locked');
                mobileTogglers.forEach(toggler => {
                    toggler.setAttribute('aria-expanded', 'true');
                });
            }
        } else {
            // Try to find it after a short delay (in case it's loaded dynamically)
            setTimeout(() => {
                const delayedWrapper = document.querySelector('.mobile-nav__wrapper') as HTMLElement;
                if (delayedWrapper) {
                    delayedWrapper.classList.add('expanded');
                    document.body.classList.add('locked');
                }
            }, 100);
        }
        
        // Also trigger jQuery handler if jQuery is available
        if (typeof (window as any).$ !== 'undefined' && (window as any).$.fn.jquery) {
            (window as any).$('.mobile-nav__wrapper').toggleClass('expanded');
            (window as any).$('body').toggleClass('locked');
        }
        
        // Reset toggle lock after a short delay
        setTimeout(() => {
            isToggling = false;
        }, 300);
    }
    
    // Global event delegation - works even if button loads late
    if (!(window as any).mobileMenuDelegationAdded) {
        // Click handler with capture phase - highest priority
        document.addEventListener('click', function(e) {
            const target = e.target as HTMLElement;
            const mobileToggler = target.closest('.mobile-nav__toggler') as HTMLElement;
            const isIcon = target.classList.contains('fa-bars') || 
                          target.classList.contains('fa') ||
                          (target.tagName === 'I' && target.closest('.mobile-nav__toggler')) ||
                          (target.closest('.main-menu__mobile-controls') && target.closest('.mobile-nav__toggler'));
        
            if (mobileToggler || isIcon) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                toggleMobileMenu(e);
                return false;
            }
        }, true); // Capture phase - fires first
    
        // Touch handler for mobile devices
        document.addEventListener('touchend', function(e) {
            const target = e.target as HTMLElement;
            const mobileToggler = target.closest('.mobile-nav__toggler') as HTMLElement;
            const isIcon = target.classList.contains('fa-bars') || 
                          target.classList.contains('fa') ||
                          (target.tagName === 'I' && target.closest('.mobile-nav__toggler')) ||
                          (target.closest('.main-menu__mobile-controls') && target.closest('.mobile-nav__toggler'));
        
            if (mobileToggler || isIcon) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                toggleMobileMenu(e);
                return false;
            }
        }, true); // Capture phase - fires first
    
        (window as any).mobileMenuDelegationAdded = true;
    }
    
    // Also attach directly to elements for immediate response
    function setupMobileMenuToggler(): void {
        const mobileTogglers = document.querySelectorAll('.mobile-nav__toggler');
        if (mobileTogglers.length === 0) return;
        
        mobileTogglers.forEach(mobileToggler => {
            const toggler = mobileToggler as HTMLElement;
            // Skip if already has our handler
            if (toggler.hasAttribute('data-menu-handler-attached')) {
                return;
            }
            
            // Ensure button is clickable
            toggler.style.pointerEvents = 'auto';
            toggler.style.cursor = 'pointer';
            toggler.style.touchAction = 'manipulation';
            toggler.style.minWidth = '44px';
            toggler.style.minHeight = '44px';
            toggler.style.zIndex = '1001';
            toggler.style.position = 'relative';
            
            // Ensure icon inside is not blocking clicks
            const icon = toggler.querySelector('i, .fa, .fa-bars') as HTMLElement;
            if (icon) {
                icon.style.pointerEvents = 'none';
            }
            
            // Add click handler
            toggler.addEventListener('click', function(e) {
                toggleMobileMenu(e);
            }, { capture: true, passive: false });
            
            // Add touch handler for mobile devices
            toggler.addEventListener('touchend', function(e) {
                toggleMobileMenu(e);
            }, { capture: true, passive: false });
            
            // Mark as having handler attached
            toggler.setAttribute('data-menu-handler-attached', 'true');
        });
    }
    
    // Try to setup immediately
    setupMobileMenuToggler();
    
    // Also try after delays in case elements aren't ready yet
    setTimeout(setupMobileMenuToggler, 100);
    setTimeout(setupMobileMenuToggler, 500);
    setTimeout(setupMobileMenuToggler, 1000);
    setTimeout(setupMobileMenuToggler, 2000);
    
    // Try when DOM is fully loaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupMobileMenuToggler);
    }
    
    // Try when window is fully loaded
    window.addEventListener('load', setupMobileMenuToggler);
    
    // Also observe for dynamically added buttons
    if (window.MutationObserver) {
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length) {
                    setupMobileMenuToggler();
                }
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// Global event delegation for mobile theme toggle (works even if button loads late)
if (!(window as any).mobileThemeToggleDelegationAdded) {
    document.addEventListener('click', function(e) {
        const target = e.target as HTMLElement;
        const themeBtn = target.closest('#theme-toggle-mobile') as HTMLElement;
        const themeIcon = target.closest('#theme-icon-mobile') as HTMLElement;
        if (themeBtn || (themeIcon && themeIcon.closest('#theme-toggle-mobile'))) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if ((window as any).NavbarContext && (window as any).NavbarContext.toggleColorMode) {
                (window as any).NavbarContext.toggleColorMode();
            } else if ((window as any).ThemeContext && (window as any).ThemeContext.toggleTheme) {
                (window as any).ThemeContext.toggleTheme();
            } else {
                const isDark = document.body.classList.contains('dark-theme');
                document.body.classList.toggle('dark-theme', !isDark);
                localStorage.setItem('themePreference', !isDark ? 'dark' : 'light');
            }
        }
    }, true); // Capture phase
    
    document.addEventListener('touchend', function(e) {
        const target = e.target as HTMLElement;
        const themeBtn = target.closest('#theme-toggle-mobile') as HTMLElement;
        const themeIcon = target.closest('#theme-icon-mobile') as HTMLElement;
        if (themeBtn || (themeIcon && themeIcon.closest('#theme-toggle-mobile'))) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if ((window as any).NavbarContext && (window as any).NavbarContext.toggleColorMode) {
                (window as any).NavbarContext.toggleColorMode();
            } else if ((window as any).ThemeContext && (window as any).ThemeContext.toggleTheme) {
                (window as any).ThemeContext.toggleTheme();
            } else {
                const isDark = document.body.classList.contains('dark-theme');
                document.body.classList.toggle('dark-theme', !isDark);
                localStorage.setItem('themePreference', !isDark ? 'dark' : 'light');
            }
        }
    }, true); // Capture phase
    
    (window as any).mobileThemeToggleDelegationAdded = true;
}

// Add mobile menu buttons (search, budget)
export function addMobileMenuButtons(): void {
    const mobileNavContainer = document.querySelector('.mobile-nav__container') as HTMLElement;
    if (mobileNavContainer && !mobileNavContainer.querySelector('.mobile-nav__actions')) {
        // Create actions container
        const actionsContainer = document.createElement('div');
        actionsContainer.className = 'mobile-nav__actions';
        actionsContainer.style.cssText = 'margin-top: 20px; padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.1); display: flex; flex-direction: column; gap: 15px;';
        
        
        // Search button
        const searchBtn = document.createElement('button');
        searchBtn.type = 'button';
        searchBtn.className = 'mobile-nav__search searcher-toggler-box';
        searchBtn.style.cssText = 'display: flex; align-items: center; gap: 10px; padding: 12px 15px; background: rgba(234, 33, 39, 0.1); border-radius: 5px; color: inherit; text-decoration: none; border: none; cursor: pointer; width: 100%; text-align: left;';
        searchBtn.innerHTML = '<i class="fal fa-search"></i> <span data-translate="search">Search</span>';
        searchBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            document.body.classList.add('search-active');
            
            // Focus on search input after popup opens
            setTimeout(() => {
                const input = document.querySelector('.search-popup input[name="search-field"]') as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            }, 300);
        });
        
        // Budget button
        const budgetBtn = document.createElement('a');
        budgetBtn.href = '/orcamento';
        budgetBtn.className = 'mobile-nav__recruitment thm-btn';
        budgetBtn.style.cssText = 'display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px 20px; text-align: center;';
        // Only include text and arrow span, remove the first dark circle span
        budgetBtn.innerHTML = '<span data-translate="navBudget" class="mobile-nav__recruitment-text">Budget</span><span class="mobile-nav__recruitment-arrow"><i class="icon-next"></i></span>';
        
        // Append buttons
        actionsContainer.appendChild(searchBtn);
        actionsContainer.appendChild(budgetBtn);
        
        // Append after the navigation list
        mobileNavContainer.appendChild(actionsContainer);
        
    }
}

// Ensure search popup close works on mobile
export function ensureSearchCloseWorks(): void {
    function closeSearchPopup(): void {
        // Remove search-active class
        document.body.classList.remove('search-active');
        
        // Also trigger jQuery handler if jQuery is available
        if (typeof (window as any).$ !== 'undefined' && (window as any).$.fn.jquery) {
            (window as any).$('body').removeClass('search-active');
        }
        
        // Clear search input and results
        const inputs = document.querySelectorAll('.search-popup input[name="search-field"]');
        inputs.forEach(input => {
            (input as HTMLInputElement).value = '';
        });
        
        const allResultsContainers = document.querySelectorAll('.search-results-container');
        allResultsContainers.forEach(container => {
            container.innerHTML = '';
        });
    }
    
    // Add native JavaScript handler for close button (click and touch)
    const closeButtons = document.querySelectorAll('.close-search');
    closeButtons.forEach(closeBtn => {
        if (!closeBtn.hasAttribute('data-close-listener')) {
            // Click handler
            closeBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                closeSearchPopup();
                return false;
            });
            
            // Touch handler for mobile
            closeBtn.addEventListener('touchend', function(e) {
                e.preventDefault();
                e.stopPropagation();
                closeSearchPopup();
                return false;
            });
            
            closeBtn.setAttribute('data-close-listener', 'true');
        }
    });
    
    // Also use event delegation for dynamically added buttons
    document.addEventListener('click', function(e) {
        const target = e.target as HTMLElement;
        const closeBtn = target.closest('.close-search') as HTMLElement;
        if (closeBtn) {
            e.preventDefault();
            e.stopPropagation();
            closeSearchPopup();
            return false;
        }
    });
    
    // Touch event delegation
    document.addEventListener('touchend', function(e) {
        const target = e.target as HTMLElement;
        const closeBtn = target.closest('.close-search') as HTMLElement;
        if (closeBtn) {
            e.preventDefault();
            e.stopPropagation();
            closeSearchPopup();
            return false;
        }
    });
    
    // Also handle color layer clicks
    const colorLayers = document.querySelectorAll('.search-popup .color-layer');
    colorLayers.forEach(layer => {
        if (!layer.hasAttribute('data-close-listener')) {
            layer.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                closeSearchPopup();
            });
            
            layer.addEventListener('touchend', function(e) {
                e.preventDefault();
                e.stopPropagation();
                closeSearchPopup();
            });
            
            layer.setAttribute('data-close-listener', 'true');
        }
    });
}

// Set up sticky navbar handlers (language, theme switchers, and search button)
export function setupStickyNavbarHandlers(): void {
    setupStickyLanguageSwitcher();
    setupStickyThemeSwitcher();
    setupStickySearchButton();
}

// Set up theme switcher for sticky navbar
function setupStickyThemeSwitcher(): void {
    const stickyThemeButton = document.querySelector('.stricky-header #theme-toggle, .stricky-header #theme-icon') as HTMLElement;
    if (stickyThemeButton && (window as any).NavbarContext) {
        // Check if we already have a listener attached
        const themeButton = stickyThemeButton.closest('button') || stickyThemeButton;
        if (themeButton.hasAttribute('data-theme-listener-attached')) {
            return; // Already has listener, don't re-attach
        }
        
        // Add click handler for sticky theme switcher
        themeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if ((window as any).NavbarContext && (window as any).NavbarContext.toggleColorMode) {
                (window as any).NavbarContext.toggleColorMode();
            }
        });
        
        // Mark as having listener attached
        themeButton.setAttribute('data-theme-listener-attached', 'true');
        
        // Update the button icon based on current theme
        updateStickyThemeSwitcher();
    }
}

// Update sticky theme switcher icon
function updateStickyThemeSwitcher(): void {
    const stickyThemeIcon = document.querySelector('.stricky-header #theme-icon') as HTMLElement;
    if (stickyThemeIcon && (window as any).NavbarContext) {
        const currentMode = (window as any).NavbarContext.mode;
        if (currentMode === 'dark') {
            stickyThemeIcon.className = 'fas fa-sun';
        } else {
            stickyThemeIcon.className = 'fas fa-moon';
        }
    }
}

// Set up search button for sticky navbar
function setupStickySearchButton(): void {
    const stickySearchButtons = document.querySelectorAll('.stricky-header .searcher-toggler-box');
    
    stickySearchButtons.forEach(button => {
        // Check if we already have a listener attached
        if (button.hasAttribute('data-search-listener-attached')) {
            return; // Already has listener, don't re-attach
        }
        
        // Add click handler for sticky search button
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            document.body.classList.add('search-active');
            
            // Focus on search input after popup opens
            setTimeout(() => {
                const input = document.querySelector('.search-popup input[name="search-field"]') as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            }, 300);
        });
        
        // Mark as having listener attached
        button.setAttribute('data-search-listener-attached', 'true');
    });
}

// Initialize everything immediately
export function initializeNavbar(): void {
    // Set up active page detection
    updateActivePage();
    
    // Set up click event listeners
    document.addEventListener('click', handleNavClick);
    
    // Set up scroll listener for sticky navbar (wrapper to ignore event parameter)
    window.addEventListener('scroll', () => initStickyNavbar());
    
    // Run sticky navbar once to check current state
    initStickyNavbar();
}


// Run immediately when this script loads
initializeNavbar();
initThemeToggle();

// Sync mobile controls after a short delay to ensure contexts are loaded
setTimeout(() => {
    syncMobileLanguageSwitcher();
    syncMobileThemeSwitcher();
    ensureMobileMenuWorks();
    ensureSearchCloseWorks();
    
    // Retry initialization multiple times to ensure it works on mobile
    setTimeout(() => {
        syncMobileThemeSwitcher();
        ensureMobileMenuWorks();
    }, 500);
    
    setTimeout(() => {
        syncMobileThemeSwitcher();
        ensureMobileMenuWorks();
    }, 1000);
    
    // Add mobile menu buttons after navigation is populated
    // Wait a bit longer to ensure script.js has populated the menu
    setTimeout(() => {
        addMobileMenuButtons();
    }, 300);
}, 100);

